/**
 * PersistenceService — Central Persistence Abstraction (AJIP-16.02.004 · WS1)
 * ---------------------------------------------------------------------
 * The single Persistence Framework surface for the Adaptive Journey
 * Platform. Future implementation packages MUST route through this
 * service — no alternative persistence path is permitted (Founder
 * additional directive).
 *
 * Feature-flag gating (double-gate)
 *   Persistence activates only when isFeatureEnabled('JOURNEYS_ENABLED')
 *   AND isFeatureEnabled('JOURNEYS_PERSISTENCE') at construction time.
 *   If either flag is false, every mutating method throws
 *   FlagDisabledError. Read-only queries return safe empty values.
 *
 * The Framework never knows where persistence lives. All storage flows
 * through the adapter contract (Founder additional directive).
 *
 * Nine verbs (per directive WS1)
 *   save, load, update, delete, listRecoverable, validate, expire,
 *   migrate, rejectUnsupported.
 *
 * Additional Framework methods
 *   attachToEngine(engine, { principalRef })      — wire lifecycle hooks
 *   detachFromEngine()                            — remove hooks
 *   checkpoint(engineContext, { principalRef, triggerKind }) — explicit save
 *   restore(persistenceRecordId, engine, principalRef)      — safe restore
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import {
  RecordNotFoundError,
  RecordExpiredError,
  ForbiddenFieldError,
  UnsupportedSchemaVersionError,
} from './PersistenceErrors';
import { assertAdapter } from './PersistenceAdapter';
import {
  createRecord,
  serialise,
  deserialise,
  withChecksum,
  verifyChecksum,
  validateRecord,
  CURRENT_SCHEMA_VERSION,
  FORBIDDEN_KEYS,
} from './PersistedRecord';
import { assertMonotonicWrite, bumpRevision } from './ConcurrencyGuard';
import { RetentionPolicy } from './RetentionPolicy';
import { runMigrations } from './SchemaMigration';
import { SavePolicy } from './SavePolicy';
import {
  runRestorationGates,
  toEngineSnapshot,
} from './RestorationPolicy';
import {
  PERSISTENCE_EVENTS,
  PersistenceObservability,
} from './PersistenceObservability';
import { OBSERVABILITY_EVENTS as ENGINE_EVENTS } from '../core/Observability';

const _now = () => Date.now();
const _uuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `pr_${_now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`);

const _keyFor = (persistenceRecordId) => `record:${persistenceRecordId}`;

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_PERSISTENCE is disabled. Service is inert.');
};

class PersistenceService {
  constructor({
    adapter,
    retentionPolicy,
    savePolicy,
    migrations = [],
    nowFn = _now,
    idFn = _uuid,
  } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED') && isFeatureEnabled('JOURNEYS_PERSISTENCE');
    if (this._enabled) {
      assertAdapter(adapter);
      if (!(retentionPolicy instanceof RetentionPolicy)) {
        throw new Error('PersistenceService requires a RetentionPolicy instance');
      }
      this._adapter = adapter;
      this._retention = retentionPolicy;
      this._savePolicy = savePolicy instanceof SavePolicy ? savePolicy : new SavePolicy();
      this._migrations = Object.freeze([...migrations]);
    } else {
      this._adapter = null;
      this._retention = null;
      this._savePolicy = null;
      this._migrations = Object.freeze([]);
    }
    this._observability = new PersistenceObservability();
    this._now = nowFn;
    this._id = idFn;
    this._lastSignatureByInstance = new Map();
    this._unsubscribes = [];
  }

  get enabled() { return this._enabled; }
  get observability() { return this._observability; }
  get adapterName() { return this._enabled ? this._adapter.name : null; }

  // ─── 9-verb surface ────────────────────────────────────────────────

  async save({ journeyDefinitionId, journeyType, currentState, currentStage = null, progress = 0, authorisedData = {}, journeyInstanceId, ownerRef = null, previousRecord = null, triggerKind = 'user' }) {
    _requireEnabled(this);
    this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_SAVE_REQUESTED, {
      journeyDefinitionId, journeyInstanceId, triggerKind,
    });
    try {
      const nowMs = this._now();
      const nextRevision = bumpRevision(previousRecord);
      const record = createRecord({
        persistenceRecordId: (previousRecord && previousRecord.persistenceRecordId) || this._id(),
        journeyInstanceId,
        journeyDefinitionId,
        journeyType,
        currentState,
        currentStage,
        progress,
        authorisedData,
        createdAt: (previousRecord && previousRecord.createdAt) || new Date(nowMs).toISOString(),
        updatedAt: new Date(nowMs).toISOString(),
        expiresAt: this._retention.computeExpiresAt(nowMs, journeyDefinitionId),
        ownerRef,
        revision: nextRevision,
        restorationStatus: 'PENDING',
      });
      assertMonotonicWrite(previousRecord, record);
      const s = serialise(record);
      await this._adapter.set(_keyFor(record.persistenceRecordId), s);
      this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_SAVE_COMPLETED, {
        persistenceRecordId: record.persistenceRecordId,
        revision: record.revision,
      });
      return record;
    } catch (e) {
      this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_SAVE_FAILED, {
        journeyDefinitionId, journeyInstanceId, error: e.name, message: e.message,
      });
      throw e;
    }
  }

  async load(persistenceRecordId) {
    _requireEnabled(this);
    this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_LOAD_REQUESTED, { persistenceRecordId });
    try {
      const raw = await this._adapter.get(_keyFor(persistenceRecordId));
      if (raw === null) throw new RecordNotFoundError(`Record '${persistenceRecordId}' not found`);
      let record = deserialise(raw);
      verifyChecksum(record);
      if (record.schemaVersion < CURRENT_SCHEMA_VERSION) {
        try {
          const outcome = runMigrations(record, this._migrations, CURRENT_SCHEMA_VERSION);
          record = withChecksum(outcome.record);
          this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_MIGRATION_COMPLETED, {
            persistenceRecordId, path: outcome.path,
          });
        } catch (e) {
          this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_MIGRATION_FAILED, {
            persistenceRecordId, error: e.name, message: e.message,
          });
          throw e;
        }
      } else if (record.schemaVersion > CURRENT_SCHEMA_VERSION) {
        throw new UnsupportedSchemaVersionError('Record schemaVersion is newer than framework supports');
      }
      this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_LOAD_COMPLETED, {
        persistenceRecordId,
        revision: record.revision,
      });
      return record;
    } catch (e) {
      this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_LOAD_FAILED, {
        persistenceRecordId, error: e.name, message: e.message,
      });
      throw e;
    }
  }

  async update({ previousRecord, patch }) {
    _requireEnabled(this);
    if (!previousRecord) throw new RecordNotFoundError('update() requires previousRecord');
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
      throw new Error('update() patch must be a plain object');
    }
    // Forbidden-key check on the patch too.
    for (const k of Object.keys(patch)) {
      if (FORBIDDEN_KEYS.map((s) => s.toLowerCase()).includes(k.toLowerCase())) {
        throw new ForbiddenFieldError(`Forbidden key in patch: '${k}'`);
      }
    }
    return this.save({
      ...previousRecord,
      authorisedData: { ...previousRecord.authorisedData, ...(patch.authorisedData || {}) },
      currentState: patch.currentState || previousRecord.currentState,
      currentStage: patch.currentStage !== undefined ? patch.currentStage : previousRecord.currentStage,
      progress: patch.progress !== undefined ? patch.progress : previousRecord.progress,
      previousRecord,
      triggerKind: patch.triggerKind || 'user',
    });
  }

  async delete(persistenceRecordId) {
    _requireEnabled(this);
    return this._adapter.remove(_keyFor(persistenceRecordId));
  }

  async listRecoverable({ principalRef = null } = {}) {
    if (!this._enabled) return [];
    const keys = await this._adapter.list('record:');
    const results = [];
    const nowMs = this._now();
    for (const key of keys) {
      try {
        const raw = await this._adapter.get(key);
        if (raw === null) continue;
        const record = deserialise(raw);
        verifyChecksum(record);
        if (this._retention.isExpired(record, nowMs)) continue;
        if (record.ownerRef !== principalRef) continue;
        results.push({
          persistenceRecordId: record.persistenceRecordId,
          journeyDefinitionId: record.journeyDefinitionId,
          currentState: record.currentState,
          progress: record.progress,
          updatedAt: record.updatedAt,
          expiresAt: record.expiresAt,
        });
      } catch (_e) {
        // A corrupt record must not poison the rest of the list.
      }
    }
    return Object.freeze(results);
  }

  validate(record) {
    _requireEnabled(this);
    validateRecord(record);
    verifyChecksum(record);
    return true;
  }

  async expire({ nowMs } = {}) {
    _requireEnabled(this);
    const t = nowMs !== undefined ? nowMs : this._now();
    const keys = await this._adapter.list('record:');
    let removed = 0;
    for (const key of keys) {
      try {
        const raw = await this._adapter.get(key);
        if (raw === null) continue;
        const record = deserialise(raw);
        if (this._retention.isExpired(record, t)) {
          await this._adapter.remove(key);
          this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_RECORD_EXPIRED, {
            persistenceRecordId: record.persistenceRecordId,
          });
          removed += 1;
        }
      } catch (_e) {
        // Corrupt records: remove aggressively.
        await this._adapter.remove(key);
        removed += 1;
      }
    }
    return removed;
  }

  migrate(record) {
    _requireEnabled(this);
    const outcome = runMigrations(record, this._migrations, CURRENT_SCHEMA_VERSION);
    return withChecksum(outcome.record);
  }

  rejectUnsupported(record) {
    _requireEnabled(this);
    if (!record || typeof record.schemaVersion !== 'number') {
      throw new UnsupportedSchemaVersionError('Record has no schemaVersion');
    }
    if (record.schemaVersion > CURRENT_SCHEMA_VERSION) {
      throw new UnsupportedSchemaVersionError('Record schemaVersion is newer than framework supports', {
        recordVersion: record.schemaVersion,
        supported: CURRENT_SCHEMA_VERSION,
      });
    }
    return true;
  }

  // ─── Framework methods ────────────────────────────────────────────

  async restore({ persistenceRecordId, engine, principalRef = null }) {
    _requireEnabled(this);
    if (!engine) throw new Error('restore() requires an engine');
    try {
      const record = await this.load(persistenceRecordId);
      runRestorationGates({
        record,
        registry: engine.registry,
        retentionPolicy: this._retention,
        principalRef,
        nowMs: this._now(),
      });
      // Dispose any existing engine context for this journey before rehydrating.
      if (engine.contextOf(record.journeyDefinitionId)) {
        engine.dispose(record.journeyDefinitionId);
      }
      const ctx = engine.restore(record.journeyDefinitionId, toEngineSnapshot(record));
      this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_RESTORE_COMPLETED, {
        persistenceRecordId: record.persistenceRecordId,
      });
      return { record, context: ctx };
    } catch (e) {
      this._observability.emit(PERSISTENCE_EVENTS.PERSISTENCE_RESTORE_FAILED, {
        persistenceRecordId, error: e.name, message: e.message,
      });
      throw e;
    }
  }

  attachToEngine(engine, { principalRef = null } = {}) {
    _requireEnabled(this);
    if (!engine || !engine.observability) throw new Error('attachToEngine requires an engine with observability');
    const journeyInstanceIdOf = (identifier) => `${principalRef || 'anon'}::${identifier}`;
    const handler = (triggerKind) => async ({ payload }) => {
      try {
        const identifier = payload && payload.identifier;
        if (!identifier) return;
        const ctx = engine.contextOf(identifier);
        if (!ctx) return;
        const snap = ctx.snapshot();
        const key = journeyInstanceIdOf(identifier);
        const decision = this._savePolicy.shouldPersist({
          triggerKind,
          currentSignature: JSON.stringify({
            s: snap.currentState, g: snap.currentStage, p: snap.progress, m: snap.metadata,
          }),
          lastSavedSignature: this._lastSignatureByInstance.get(key),
          nowMs: this._now(),
          key,
        });
        if (!decision.save) return;
        // Load previous record if we have one (for revision monotonicity).
        // For simplicity in this package, we key persistenceRecordId to
        // journeyInstanceId one-to-one via a deterministic identifier.
        const previousRecord = null;
        await this.save({
          journeyDefinitionId: identifier,
          journeyType: identifier,
          journeyInstanceId: key,
          currentState: snap.currentState,
          currentStage: snap.currentStage,
          progress: snap.progress,
          authorisedData: { ...snap.metadata },
          ownerRef: principalRef,
          previousRecord,
          triggerKind,
        });
        this._savePolicy.recordSave(this._now(), key);
        this._lastSignatureByInstance.set(key, JSON.stringify({
          s: snap.currentState, g: snap.currentStage, p: snap.progress, m: snap.metadata,
        }));
      } catch (_e) {
        // Save-attempt failures must never affect engine behaviour.
      }
    };
    this._unsubscribes.push(engine.observability.on(ENGINE_EVENTS.JOURNEY_STARTED,   handler('transition')));
    this._unsubscribes.push(engine.observability.on(ENGINE_EVENTS.JOURNEY_PAUSED,    handler('pause')));
    this._unsubscribes.push(engine.observability.on(ENGINE_EVENTS.JOURNEY_RESUMED,   handler('transition')));
    this._unsubscribes.push(engine.observability.on(ENGINE_EVENTS.JOURNEY_COMPLETED, handler('transition')));
    this._unsubscribes.push(engine.observability.on(ENGINE_EVENTS.JOURNEY_CANCELLED, handler('transition')));
    this._unsubscribes.push(engine.observability.on(ENGINE_EVENTS.JOURNEY_FAILED,    handler('transition')));
    return true;
  }

  detachFromEngine() {
    for (const off of this._unsubscribes) {
      try { off(); } catch (_e) { /* isolate */ }
    }
    this._unsubscribes = [];
  }

  async healthCheck() {
    if (!this._enabled) return { ok: true, active: false, reason: 'flag_disabled' };
    const adapter = await this._adapter.healthCheck();
    return { ok: adapter.ok, active: true, adapter };
  }
}

export { PersistenceService };
